import { useUpdateCandidate } from "@vidario/api-client";
import { type Candidate } from "@vidario/domain";
import { Button, Card, HiddenBlock, Markdown, Textarea, useToast } from "@vidario/ui";
import { Check, Lock, Pencil } from "lucide-react";
import { useState, type ReactElement, type ReactNode } from "react";

import { useAnonymView } from "../../anonym/useAnonymView";
import { HIDDEN_NOTES_TEXT } from "../../anonym/copy";

/** Admin-only container for §4.7 (raw interview notes) + §4.7a
 * (aufbereitete Interview-Key-Findings). Both sections follow the
 * view-until-click markdown pattern. Key-Findings persist as a newline-
 * separated list and render as rich markdown in the Empfehlung + PDF. */
export function InterviewCard({
  jobId,
  candidate,
  findingsEditable = true,
}: {
  jobId: string;
  candidate: Candidate;
  /** False on a closed job: key findings are frozen, raw notes stay editable (#19). */
  findingsEditable?: boolean;
}): ReactElement {
  const update = useUpdateCandidate();
  const toast = useToast();
  const anonym = useAnonymView();

  const [editingRaw, setEditingRaw] = useState(false);
  const [rawDraft, setRawDraft] = useState(candidate.interviewNotes ?? "");

  const [editingFindings, setEditingFindings] = useState(false);
  const [findingsDraft, setFindingsDraft] = useState(candidate.interviewKeyFindings.join("\n"));

  const saveRaw = (): void => {
    update.mutate(
      { jobId, candidateId: candidate.id, patch: { interviewNotes: rawDraft || null } },
      {
        onSuccess: () => {
          setEditingRaw(false);
          toast.push({
            kind: "success",
            title: "Rohnotizen gespeichert",
            message: "Sichtbar nur für Admins.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const cancelRaw = (): void => {
    setRawDraft(candidate.interviewNotes ?? "");
    setEditingRaw(false);
  };

  const saveFindings = (): void => {
    const lines = findingsDraft
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    update.mutate(
      { jobId, candidateId: candidate.id, patch: { interviewKeyFindings: lines } },
      {
        onSuccess: () => {
          setEditingFindings(false);
          toast.push({
            kind: "success",
            title: "Key-Findings gespeichert",
            message: "Sichtbar in der Empfehlung + PDF-Export.",
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  const cancelFindings = (): void => {
    setFindingsDraft(candidate.interviewKeyFindings.join("\n"));
    setEditingFindings(false);
  };

  if (anonym) {
    return (
      <Card>
        <h3 className="m-0 mb-3 text-[15px] font-semibold">Interview</h3>
        <HiddenBlock>{HIDDEN_NOTES_TEXT}</HiddenBlock>
      </Card>
    );
  }

  return (
    <div
      className="rounded-md py-4 pl-3 pr-0 bg-[linear-gradient(to_right,var(--admin-marker-bg),transparent_40%)] border-l-[3px] border-[color:var(--admin-marker)]"
      data-testid="interview-admin-block"
    >
      <div className="ml-1 mb-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--admin-marker)]">
        <Lock size={10} /> Vidario-intern
      </div>

      <Card>
        <SectionHeader
          title="Interview-Notizen (Rohfassung)"
          editing={editingRaw}
          onBeginEdit={() => setEditingRaw(true)}
        />
        {editingRaw ? (
          <>
            <Textarea
              aria-label="Interview-Rohnotizen"
              rows={6}
              placeholder="Stichpunkte, O-Töne, Bauchgefühl, Detailbeobachtungen… (Markdown)"
              value={rawDraft}
              onChange={(e) => setRawDraft(e.target.value)}
            />
            <MarkdownHintRow
              left="**fett** · *kursiv* · - Liste · [Link](https://…)"
              onCancel={cancelRaw}
              onSave={saveRaw}
              saving={update.isPending}
            />
          </>
        ) : (
          <Markdown empty="Noch keine Rohnotizen.">{candidate.interviewNotes ?? ""}</Markdown>
        )}
      </Card>

      <Card className="mt-3">
        <SectionHeader
          title={
            <>
              Interview-Key-Findings{" "}
              <span className="ml-1 text-[11px] font-medium text-text-tertiary">
                (für Kunde sichtbar)
              </span>
            </>
          }
          editing={editingFindings}
          canEdit={findingsEditable}
          onBeginEdit={() => setEditingFindings(true)}
        />
        {editingFindings ? (
          <>
            <Textarea
              aria-label="Interview-Key-Findings"
              rows={5}
              placeholder={
                "Ein Bullet pro Zeile oder Markdown.\n- Verfügbar ab 01.07.2026\n- Gehaltswunsch 105k + ESOP"
              }
              value={findingsDraft}
              onChange={(e) => setFindingsDraft(e.target.value)}
            />
            <MarkdownHintRow
              left="Jede Zeile wird als eigener Punkt übernommen."
              onCancel={cancelFindings}
              onSave={saveFindings}
              saving={update.isPending}
            />
          </>
        ) : candidate.interviewKeyFindings.length === 0 ? (
          <p className="m-0 text-sm italic text-text-tertiary">
            Noch keine Key-Findings hinterlegt.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {candidate.interviewKeyFindings.map((f, i) => (
              <li key={i} className="flex gap-2 text-[13px] leading-relaxed">
                <Check size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-success-600" />
                <Markdown className="text-[13px]">{f}</Markdown>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SectionHeader({
  title,
  editing,
  canEdit = true,
  onBeginEdit,
}: {
  title: ReactNode;
  editing: boolean;
  canEdit?: boolean;
  onBeginEdit: () => void;
}): ReactElement {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-3">
      <h3 className="m-0 text-sm font-semibold">{title}</h3>
      {canEdit && !editing && (
        <button
          type="button"
          onClick={onBeginEdit}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <Pencil size={12} /> Bearbeiten
        </button>
      )}
    </div>
  );
}

function MarkdownHintRow({
  left,
  onCancel,
  onSave,
  saving,
}: {
  left: string;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}): ReactElement {
  return (
    <div className="mt-2 flex items-center justify-between">
      <span className="text-xs text-text-tertiary">{left}</span>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button size="sm" loading={saving} onClick={onSave}>
          Speichern
        </Button>
      </div>
    </div>
  );
}
